import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  motion,
  useScroll,
  useTransform,
  useInView,
  useMotionValue,
  useSpring,
  AnimatePresence,
} from 'framer-motion';
import { ChevronRight, Ticket } from 'lucide-react';

import axios from 'axios';
import { heroMedia, DEFAULT_VIDEO, DEFAULT_POSTER } from '../../utils/heroMedia';

/* ------------------------------------------------------------------ */
/*  Character-by-character split reveal                                */
/* ------------------------------------------------------------------ */
const titleContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0.25,
      staggerChildren: 0.04,
    },
  },
};

const titleChar = {
  hidden: {
    opacity: 0,
    y: '0.6em',
    rotateX: -45,
    filter: 'blur(10px)',
  },
  visible: {
    opacity: 1,
    y: 0,
    rotateX: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.9,
      ease: [0.2, 0.8, 0.2, 1],
    },
  },
};

const SplitText = ({ text, className = '', variants = titleChar }) => {
  const words = text.split(' ');
  return (
    <span className={`inline-block ${className}`} aria-label={text}>
      {words.map((word, wi) => (
        <span
          key={`w-${wi}`}
          className="inline-block whitespace-nowrap"
          style={{ perspective: 600 }}
        >
          {Array.from(word).map((char, ci) => (
            <motion.span
              key={`c-${wi}-${ci}`}
              variants={variants}
              className="inline-block"
              style={{
                willChange: 'transform, opacity, filter',
                transformOrigin: '50% 100%',
              }}
            >
              {char}
            </motion.span>
          ))}
          {wi < words.length - 1 && (
            <span className="inline-block" style={{ width: '0.25em' }}>
              &nbsp;
            </span>
          )}
        </span>
      ))}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/*  Magnetic CTA button                                                */
/* ------------------------------------------------------------------ */
// Cache motion components to avoid recreating them on every render
const motionLink = motion.create ? motion.create(Link) : motion(Link);

const MagneticButton = ({ children, className = '', as = 'button', ...rest }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 180, damping: 18, mass: 0.4 });
  const sy = useSpring(my, { stiffness: 180, damping: 18, mass: 0.4 });

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = e.clientX - (r.left + r.width / 2);
    const y = e.clientY - (r.top + r.height / 2);
    mx.set(x * 0.25);
    my.set(y * 0.35);
  };

  const handleLeave = () => {
    mx.set(0);
    my.set(0);
  };

  // Resolve the motion component:
  // - If `as` is a string like 'a' or 'button', use motion['a'] / motion.button
  // - If `as` is a React component (like Link), use the cached motionLink
  // - Fallback to motion.button
  let Comp;
  if (typeof as === 'string') {
    Comp = motion[as] || motion.button;
  } else if (as === Link) {
    Comp = motionLink;
  } else {
    Comp = motion.button;
  }

  return (
    <Comp
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={{ x: sx, y: sy, willChange: 'transform' }}
      className={className}
      {...rest}
    >
      {children}
    </Comp>
  );
};

/* ------------------------------------------------------------------ */
/*  HeroSection                                                        */
/* ------------------------------------------------------------------ */
const HeroSection = () => {
  const { t } = useTranslation();
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const contentRef = useRef(null);

  const [settings, setSettings] = useState({});
  const [desktop, setDesktop] = useState(() => window.matchMedia('(min-width: 768px)').matches);
  const media = heroMedia(settings, desktop ? 'desktop' : 'mobile');
  useEffect(() => {
    const query = window.matchMedia('(min-width: 768px)');
    const update = () => setDesktop(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  const [videoFailed, setVideoFailed] = useState(false);
  const [posterFailed, setPosterFailed] = useState(false);
  const videoUrl = videoFailed ? DEFAULT_VIDEO : media.video;
  const posterUrl = posterFailed ? DEFAULT_POSTER : media.poster;
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    axios.get(`${process.env.REACT_APP_BACKEND_URL || ''}/api/settings`, { signal: controller.signal })
      .then(({ data }) => setSettings(data))
      .catch(() => {}); // Keep the bundled media when settings are unavailable.
    return () => controller.abort();
  }, []);

  useEffect(() => { setVideoLoaded(false); }, [videoUrl]);
  useEffect(() => { setVideoFailed(false); setPosterFailed(false); }, [media.video, media.poster]);
  const [hasScrolled, setHasScrolled] = useState(false);

  const inView = useInView(contentRef, { once: true, amount: 0.3 });

  /* ----------- parallax on the video background ----------- */
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '-8%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);
  const scrollIndicatorOpacity = useTransform(
    scrollYProgress,
    [0, 0.12],
    [1, 0]
  );

  /* ----------- play / pause video when in view ----------- */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.25 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [videoUrl]);

  /* ----------- hide scroll indicator after any scroll ----------- */
  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 40) setHasScrolled(true);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToContent = () => {
    window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
  };

  /* ----------- orchestration ----------- */
  const orchestrate = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.2,
        staggerChildren: 0.18,
      },
    },
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 32, filter: 'blur(8px)' },
    visible: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: { duration: 0.9, ease: [0.2, 0.8, 0.2, 1] },
    },
  };

  const fadeRight = {
    hidden: { opacity: 0, x: 40, letterSpacing: '0.05em' },
    visible: {
      opacity: 1,
      x: 0,
      letterSpacing: '0.3em',
      transition: { duration: 1.1, ease: [0.2, 0.8, 0.2, 1] },
    },
  };

  return (
    <section
      ref={sectionRef}
      className="relative h-[100svh] min-h-[560px] w-full overflow-hidden bg-[#060606]"
      data-testid="hero-section"
    >
      {/* ============== BACKGROUND ============== */}
      <div className="absolute inset-0 z-0 flex items-center justify-center">
        {/* Keep the entire mobile frame; restore the original desktop background framing. */}
        <img
          src={posterUrl}
          alt="Invasion Latina"
          className={`absolute inset-0 h-full w-full object-contain md:object-cover transition-opacity duration-1000 ${
            videoLoaded ? 'opacity-0' : 'opacity-100'
          }`}
          onError={(e) => {
            setPosterFailed(true);
          }}
        />
        <video
          key={videoUrl}
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-contain md:object-cover"
          src={videoUrl}
          poster={posterUrl}
          muted
          loop
          playsInline
          preload="metadata"
          onPlaying={() => setVideoLoaded(true)}
          onError={(e) => {
            setVideoLoaded(false);
            if (videoUrl !== DEFAULT_VIDEO) setVideoFailed(true);
            else e.currentTarget.style.display = 'none';
          }}
          data-testid="hero-video"
        />
      </div>

      {/* ============== OVERLAYS ============== */}
      {/* Only shade the controls; keep the footage sharp and its colours intact. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-24 bg-gradient-to-b from-black/70 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-48 bg-gradient-to-t from-black/80 to-transparent" />

      {/* ============== CONTENT ============== */}
      <motion.div
        ref={contentRef}
        style={{ y: contentY, opacity: contentOpacity, willChange: 'transform, opacity' }}
        variants={orchestrate}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        className="relative z-10 flex h-full w-full flex-col items-center justify-end px-4 pb-20 sm:pb-16 text-center"
      >
        {/* Double CTA — only content on the hero */}
        <motion.div
          variants={fadeUp}
          className="flex flex-col items-center gap-4 sm:flex-row sm:gap-5"
        >
          {/* Primary — TICKETS */}
          <MagneticButton
            as="a"
            href="#tickets"
            className="group relative inline-flex items-center gap-3 overflow-hidden rounded-full border border-[#C9A84C] bg-[#C9A84C] px-10 py-4 font-display text-base uppercase tracking-[0.25em] text-[#060606] shadow-[0_10px_40px_-10px_rgba(201,168,76,0.8)] transition-[background,box-shadow] duration-300 hover:bg-[#E2C56A] hover:shadow-[0_15px_55px_-8px_rgba(226,197,106,0.9)] md:text-lg"
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            data-testid="hero-primary-ticket-button"
          >
            {/* Shimmer sweep */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full"
            />
            <Ticket className="relative z-10 h-5 w-5" strokeWidth={2.25} />
            <span className="relative z-10">{t('hero.cta_tickets')}</span>
          </MagneticButton>

          {/* Secondary — AGENDA */}
          <MagneticButton
            as={Link}
            to="/agenda"
            className="group relative inline-flex items-center gap-2 rounded-full border border-[#C9A84C]/60 bg-white/[0.03] px-9 py-4 font-display text-base uppercase tracking-[0.25em] text-[#F5F0E8] backdrop-blur-sm transition-colors duration-300 hover:border-[#C9A84C] hover:bg-white/[0.08] md:text-lg"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            data-testid="hero-secondary-agenda-button"
          >
            <span>{t('nav.agenda') || 'Agenda'}</span>
            <motion.span
              className="inline-flex"
              initial={{ x: 0 }}
              whileHover={{ x: 4 }}
            >
              <ChevronRight
                className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1"
                strokeWidth={2.25}
              />
            </motion.span>
          </MagneticButton>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
